import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, FileText, Calendar } from "lucide-react";

const RESPONSABLE = "Daniela Muñoz Jiménez";
const SITIO = "https://siempresalud.mx";
const CORREO_PRIVACIDAD = "privacidad@siempresalud.mx";
const FECHA_ACTUALIZACION = "10 de febrero de 2026";

const SECCIONES = [
  { id: "responsable", label: "1. Responsable" },
  { id: "datos", label: "2. Datos personales que recabamos" },
  { id: "sensibles", label: "3. Datos personales sensibles de salud" },
  { id: "finalidades", label: "4. Finalidades del tratamiento" },
  { id: "fundamento", label: "5. Fundamento legal" },
  { id: "transferencias", label: "6. Transferencias y encargados" },
  { id: "seguridad", label: "7. Medidas de seguridad" },
  { id: "conservacion", label: "8. Conservación de datos" },
  { id: "arco", label: "9. Derechos ARCO y revocación" },
  { id: "cookies", label: "10. Cookies y tecnologías similares" },
  { id: "menores", label: "11. Menores de edad" },
  { id: "cambios", label: "12. Cambios al aviso" },
  { id: "inai", label: "13. INAI y autoridades" },
];

export default function AvisoPrivacidad() {
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="page-aviso-privacidad">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-10">
        <LegalHero
          eyebrow="Aviso legal"
          title="Aviso de Privacidad Integral"
          subtitle="Conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP) y su Reglamento."
          fecha={FECHA_ACTUALIZACION}
          icon={<Shield size={22} />}
        />

        <TocIndex />

        <article className="mt-8 rounded-xl border border-border bg-white p-6 sm:p-8 space-y-8 text-sm text-foreground leading-relaxed">
          <Sec id="responsable" title="1. Responsable del tratamiento de datos personales">
            <p>
              <b>{RESPONSABLE}</b>, en lo sucesivo &ldquo;el Responsable&rdquo; o &ldquo;Siempre Salud&rdquo;,
              con domicilio para efectos del presente aviso en los Estados Unidos
              Mexicanos y sitio web oficial <a className="link" href={SITIO}>siempresalud.mx</a>, es
              responsable del uso y protección de tus datos personales conforme al presente
              Aviso de Privacidad.
            </p>
            <p>
              Para el ejercicio de tus derechos, dudas o quejas relacionadas con el tratamiento
              de tus datos personales, ponemos a tu disposición el correo{" "}
              <a className="link" href={`mailto:${CORREO_PRIVACIDAD}`}>{CORREO_PRIVACIDAD}</a>.
            </p>
          </Sec>

          <Sec id="datos" title="2. Datos personales que recabamos">
            <p>
              Para brindarte el servicio, podemos recabar las siguientes categorías de datos
              personales:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Datos de identificación:</b> nombre completo, sexo, edad, fecha de nacimiento.</li>
              <li><b>Datos de contacto:</b> correo electrónico, teléfono de contacto.</li>
              <li><b>Datos de autenticación:</b> contraseñas (almacenadas en formato hash irreversible), identificadores de Google Sign-In cuando aplique.</li>
              <li><b>Datos profesionales</b> (solo médicos): cédula profesional, cédula de especialidad, especialidad, dirección del consultorio, aviso de funcionamiento COFEPRIS, título profesional y demás documentos de acreditación.</li>
              <li><b>Datos de facturación y pago:</b> procesados directamente por nuestro proveedor de pagos (Stripe). Siempre Salud NO almacena números de tarjeta ni información completa del medio de pago.</li>
              <li><b>Datos técnicos y de auditoría:</b> dirección IP, agente de usuario, fecha y hora de acceso, registros de eventos de seguridad.</li>
            </ul>
          </Sec>

          <Sec id="sensibles" title="3. Datos personales sensibles de salud">
            <p>
              <b>Consentimiento expreso para datos sensibles.</b> Al utilizar el servicio de
              orientación clínica con inteligencia artificial, otorgas tu <b>consentimiento
              expreso</b> (artículos 8 y 9 de la LFPDPPP) para el tratamiento de los siguientes
              datos personales sensibles:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Historia clínica: peso, altura, alergias, enfermedades crónicas, medicación actual, condiciones especiales, hábitos.</li>
              <li>Síntomas, molestias o cuadros clínicos que describas.</li>
              <li>Resultados de laboratorio o estudios médicos que decidas cargar.</li>
              <li>Las respuestas generadas por la IA en cada consulta.</li>
            </ul>
            <p>
              Puedes revocar este consentimiento en cualquier momento a través de los medios
              descritos en la sección de <a className="link" href="#arco">Derechos ARCO</a>.
              La revocación implicará que dejarás de tener acceso al servicio de orientación
              clínica, ya que su prestación depende del tratamiento de estos datos.
            </p>
          </Sec>

          <Sec id="finalidades" title="4. Finalidades del tratamiento">
            <h3 className="font-semibold text-[#0F2942] mt-2">Finalidades primarias (necesarias para la prestación del servicio)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Crear y gestionar tu cuenta y verificar tu identidad.</li>
              <li>Prestar el servicio de orientación clínica generada por inteligencia artificial, basada exclusivamente en Guías de Práctica Clínica de México y medicamentos de venta libre (OTC).</li>
              <li>Procesar los pagos correspondientes a la consulta IA y a las suscripciones de especialistas.</li>
              <li>Publicar los datos profesionales de médicos en nuestro Directorio de Especialistas para que las personas usuarias puedan contactarlos.</li>
              <li>Mantener registros de auditoría de seguridad y cumplimiento.</li>
              <li>Atender solicitudes, quejas y ejercicio de derechos ARCO.</li>
              <li>Dar cumplimiento a obligaciones legales, regulatorias o requerimientos de autoridades competentes.</li>
            </ul>

            <h3 className="font-semibold text-[#0F2942] mt-4">Finalidades secundarias (no necesarias, requieren tu consentimiento)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Mejora del servicio mediante análisis agregado y anonimizado de uso.</li>
              <li>Envío de comunicaciones informativas, boletines y contenido educativo relacionado con la salud.</li>
              <li>Encuestas de satisfacción y estudios de calidad del servicio.</li>
            </ul>
            <p>
              Si no deseas que tus datos se traten para las finalidades secundarias, puedes
              manifestarlo enviando un correo a{" "}
              <a className="link" href={`mailto:${CORREO_PRIVACIDAD}`}>{CORREO_PRIVACIDAD}</a>{" "}
              con el asunto &ldquo;Negativa a finalidades secundarias&rdquo;.
            </p>
          </Sec>

          <Sec id="fundamento" title="5. Fundamento legal">
            <p>
              El tratamiento de tus datos personales se realiza con fundamento en los
              artículos 8, 9, 15, 16 y 17 de la LFPDPPP; su Reglamento; los Lineamientos del
              Aviso de Privacidad emitidos por la Secretaría de Economía; la NOM-004-SSA3-2012
              del Expediente Clínico; la NOM-024-SSA3-2012 de Sistemas de Información de
              Registro Electrónico para la Salud; y demás normatividad aplicable en materia
              de salud y protección de datos.
            </p>
          </Sec>

          <Sec id="transferencias" title="6. Transferencias y encargados de tratamiento">
            <p>
              Para prestar el servicio, tus datos son procesados por los siguientes terceros
              en calidad de <b>encargados</b> (artículo 49 del Reglamento de la LFPDPPP), bajo
              contratos que exigen niveles equivalentes de protección:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <b>Stripe, Inc.</b> — procesamiento de pagos con tarjeta y suscripciones.
                Stripe recibe únicamente la información necesaria para procesar el cobro
                (correo, monto, país). Siempre Salud NO almacena números de tarjeta.
              </li>
              <li>
                <b>Google LLC (Gemini API / Vertex AI)</b> — motor de inteligencia artificial
                para la generación de recomendaciones clínicas. Los datos clínicos que
                describes se envían cifrados en tránsito (TLS 1.2+) para su procesamiento.
              </li>
              <li>
                <b>Cloudinary, Inc.</b> — almacenamiento seguro de archivos que decidas
                cargar (por ejemplo, PDFs de estudios de laboratorio o documentos
                profesionales de médicos), en modalidad de acceso autenticado.
              </li>
              <li>
                <b>Resend, Inc.</b> — envío de correos electrónicos de verificación, códigos
                de un solo uso (OTP) y notificaciones transaccionales.
              </li>
              <li>
                <b>Emergent (proveedor de infraestructura)</b> — hospedaje del backend, base
                de datos MongoDB y frontend web dentro del territorio bajo controles de
                seguridad estándar de la industria.
              </li>
            </ul>
            <p>
              No realizamos transferencias de datos personales a otros terceros con
              finalidades distintas a las descritas sin recabar previamente tu consentimiento,
              salvo las excepciones previstas en el artículo 37 de la LFPDPPP.
            </p>
          </Sec>

          <Sec id="seguridad" title="7. Medidas de seguridad">
            <p>
              Implementamos medidas de seguridad administrativas, técnicas y físicas
              razonables para proteger tus datos personales contra daño, pérdida, alteración,
              destrucción o uso, acceso o tratamiento no autorizado:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><b>Cifrado en reposo (AES-256-GCM)</b> de tu historia clínica, síntomas, respuestas de IA y otros campos sensibles en la base de datos.</li>
              <li><b>Cifrado en tránsito (TLS 1.2+)</b> en todas las comunicaciones entre tu navegador y nuestros servidores, y hacia nuestros encargados.</li>
              <li>Hasheo irreversible de contraseñas con algoritmos modernos (bcrypt).</li>
              <li>URLs firmadas de corta duración para el acceso a documentos privados.</li>
              <li>Registros de auditoría de accesos y eventos de seguridad.</li>
              <li>Control de acceso por roles (persona usuaria, médico, administrador) con principio de mínimo privilegio.</li>
              <li>
                <b>Acceso administrativo autorizado</b>: personal administrativo debidamente autorizado
                del Responsable puede acceder a datos personales y de salud <b>únicamente</b> para fines
                de soporte, supervisión y cumplimiento normativo, con <b>registro de auditoría inmutable</b>
                de cada acceso, conforme al principio de mínimo privilegio.
              </li>
            </ul>
            <p>
              Ninguna medida de seguridad es infalible. En caso de una vulneración de
              seguridad significativa que afecte tus derechos patrimoniales o morales, el
              Responsable te notificará conforme al artículo 20 de la LFPDPPP.
            </p>
          </Sec>

          <Sec id="conservacion" title="8. Conservación de datos">
            <p>
              Conservaremos tus datos personales durante el tiempo que exista una relación
              contractual o de servicio contigo, y posteriormente por los plazos que
              establezcan las obligaciones fiscales, legales y regulatorias aplicables (por
              ejemplo, el resguardo del expediente clínico conforme a NOM-004-SSA3-2012).
              Transcurridos dichos plazos, los datos serán cancelados y suprimidos conforme
              a políticas internas de gobierno de datos.
            </p>
          </Sec>

          <Sec id="arco" title="9. Derechos ARCO y revocación del consentimiento">
            <p>
              Tienes derecho a <b>Acceder, Rectificar, Cancelar u Oponerte</b> al tratamiento
              de tus datos personales, así como a revocar el consentimiento que hayas
              otorgado.
            </p>
            <p>
              Para ejercer cualquiera de estos derechos, envía tu solicitud al correo{" "}
              <a className="link" href={`mailto:${CORREO_PRIVACIDAD}`}>{CORREO_PRIVACIDAD}</a>{" "}
              acompañada de:
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Nombre completo del titular y correo con el que se creó la cuenta.</li>
              <li>Documento oficial vigente que acredite tu identidad (INE, pasaporte o cédula profesional).</li>
              <li>Descripción clara y precisa del derecho que deseas ejercer y los datos sobre los que recae.</li>
              <li>Cualquier documento que facilite la localización de tus datos.</li>
            </ol>
            <p>
              El Responsable atenderá tu solicitud en un plazo máximo de <b>20 días hábiles</b>{" "}
              contados a partir de su recepción. En caso de resultar procedente, se hará
              efectiva dentro de los <b>15 días hábiles</b> siguientes.
            </p>
          </Sec>

          <Sec id="cookies" title="10. Cookies y tecnologías similares">
            <p>
              Utilizamos cookies estrictamente necesarias para el funcionamiento del sitio
              (mantener tu sesión iniciada, recordar preferencias mínimas). No utilizamos
              cookies de publicidad ni de perfilamiento de terceros. Puedes deshabilitar las
              cookies desde tu navegador, en el entendido de que ello podría afectar el
              funcionamiento del servicio.
            </p>
          </Sec>

          <Sec id="menores" title="11. Menores de edad">
            <p>
              El servicio está dirigido a personas mayores de 18 años. Los datos de personas
              menores de edad únicamente podrán ser tratados con el consentimiento expreso
              de quien ejerza la patria potestad o tutela, conforme al artículo 8 de la
              LFPDPPP y al principio del interés superior del menor.
            </p>
          </Sec>

          <Sec id="cambios" title="12. Cambios al presente aviso">
            <p>
              El Responsable podrá modificar el presente Aviso de Privacidad en cualquier
              momento. Las modificaciones sustanciales se comunicarán a través de esta misma
              página o por correo electrónico a la dirección registrada en tu cuenta. La
              fecha de la última actualización aparece al inicio y al final del documento.
            </p>
          </Sec>

          <Sec id="inai" title="13. INAI y autoridades competentes">
            <p>
              Si consideras que tu derecho a la protección de datos personales ha sido
              vulnerado o has recibido una respuesta insatisfactoria del Responsable, puedes
              acudir al{" "}
              <b>Instituto Nacional de Transparencia, Acceso a la Información y Protección
              de Datos Personales (INAI)</b> mediante su portal{" "}
              <a
                className="link"
                href="https://home.inai.org.mx/"
                target="_blank"
                rel="noopener noreferrer"
              >
                home.inai.org.mx
              </a>{" "}
              o al teléfono <span className="font-mono">55 5004 2400</span>.
            </p>
          </Sec>

          <div className="pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-muted-foreground">
            <div>
              <Calendar size={12} className="inline mr-1" />
              Última actualización: <b>{FECHA_ACTUALIZACION}</b>
            </div>
            <div>Responsable: {RESPONSABLE}</div>
          </div>
        </article>

        <div className="mt-6 flex items-center justify-between">
          <Link to="/" data-testid="aviso-back-btn">
            <Button variant="outline" size="sm">
              <ArrowLeft size={14} /> Volver al inicio
            </Button>
          </Link>
          <Link to="/terminos-y-condiciones" data-testid="aviso-to-terminos-link">
            <Button variant="ghost" size="sm" className="text-[#2563EB]">
              <FileText size={14} /> Términos y Condiciones
            </Button>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function LegalHero({ eyebrow, title, subtitle, fecha, icon }) {
  return (
    <section className="rounded-xl border border-border bg-white p-6 sm:p-8">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2xl">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB]">
            {eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#0F2942] tracking-tight mt-1">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{subtitle}</p>
          )}
          <div className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
            <Calendar size={12} /> Última actualización: <b>{fecha}</b>
          </div>
        </div>
        <div className="w-10 h-10 rounded-lg bg-[#0F2942]/5 text-[#0F2942] grid place-items-center shrink-0">
          {icon}
        </div>
      </div>
    </section>
  );
}

function TocIndex() {
  return (
    <nav className="mt-6 rounded-xl border border-border bg-white p-5" aria-label="Índice del documento" data-testid="aviso-toc">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        Contenido
      </div>
      <ol className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
        {SECCIONES.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="text-[#0F2942] hover:text-[#2563EB] hover:underline decoration-dotted underline-offset-4"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Sec({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`aviso-sec-${id}`}>
      <h2 className="font-display text-lg sm:text-xl font-semibold text-[#0F2942] tracking-tight">
        {title}
      </h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}
